import {
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import {
    webLibSdk,
    trimmedAddress,
    getFormatterAllocationType,
    formatPrice,
    getCurrentTimeZoneDate,
} from 'helpers';
import CopyButton from 'components/UI/CopyButton';
import { useAllocationHistory } from 'services';
import { CUSTOM_ALLOCATION_TYPE } from '_constants/allocationTypes';

const useHistoryData = (filterDate, addressSearch, distributionAllocationType) => {
    const [history, setHistory] = useState([]);
    const [totalRows, setTotalRows] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [page, setPage] = useState(1);

    const isCustom = useMemo(() => distributionAllocationType === CUSTOM_ALLOCATION_TYPE, [distributionAllocationType]);

    const {
        isLoading,
        getHistory,
    } = useAllocationHistory();

    const columns = useMemo(() => [
        {
            name: 'From',
            selector: ({ data, _id }) => (
                <div className="address-column">
                    <a
                        href={webLibSdk.getTransactionUrl(data.from, true)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {trimmedAddress(data.from, 10, 10)}
                    </a>
                    {(data.from) && (
                        <CopyButton
                            key={`${_id}-copy-address-from`}
                            noText
                            text={data.from}
                        />
                    )}
                </div>
            ),
            center: true,
            width: '230px',
        },
        {
            name: 'Recipient',
            selector: ({ data, _id }) => (
                <div className="address-column">
                    <a
                        href={webLibSdk.getTransactionUrl(data.recipient || data.account, true)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {trimmedAddress((data.recipient || data.account), 10, 10)}
                    </a>
                    <CopyButton
                        key={`${_id}-copy-address-recipient`}
                        noText
                        text={data.recipient || data.account}
                    />
                </div>
            ),
            center: true,
            width: '230px',
        },
        {
            name: 'Transaction Hash',
            selector: (row) => (
                <a
                    href={webLibSdk.getTransactionUrl(row.txHash, false)}
                    target="_blank"
                    rel="noreferrer"
                >
                    {trimmedAddress(row.txHash, 10, 10)}
                </a>
            ),
            center: true,
            width: '206px',
        },
        {
            name: 'Event',
            selector: ({ data }) => data.name,
            sortable: true,
            center: true,
            width: '210px',
        },
        {
            name: 'Allocation Type',
            selector: ({ data }) => {
                const displayType = isCustom && data.allocationType ? 'Custom Sale' : data.allocationType;
                return (<p className="allocatedType">{(displayType ? getFormatterAllocationType(displayType) : '-----')}</p>);
            },
            center: true,
            width: '150px',
        },
        {
            name: 'Amount',
            selector: ({ data }) => ((data.amount || data.amountClamed) ? formatPrice(data.amount || data.amountClamed) : '-----'),
            sortable: true,
            center: true,
            sortFunction: (rowA, rowB) => {
                const a = Number(rowA.data.amount || 0);
                const b = Number(rowB.data.amount || 0);

                return a > b ? 1 : -1;
            },
        },
        {
            name: 'Date',
            selector: ({ data }) => getCurrentTimeZoneDate(data.date),
            width: '190px',
            sortable: true,
            center: true,
        },
    ], [isCustom]);

    const filteredData = useMemo(() => {
        if (!addressSearch) return history;

        return history.filter(({ data }) => !addressSearch || data.from.toLowerCase().includes(addressSearch?.toLowerCase()) || data?.recipient?.toLowerCase().includes(addressSearch?.toLowerCase()));
    }, [addressSearch, history]);

    const getData = useCallback(async () => {
        const offset = (page - 1) * perPage;
        const { data: { data } } = await getHistory(isCustom, offset, perPage, filterDate);

        setHistory(data.result);
        setTotalRows(data.count);
    }, [filterDate, getHistory, isCustom, page, perPage]);

    useEffect(() => {
        getData();
    }, [getData]);

    return {
        columns,
        totalRows,
        isLoading,
        filteredData,
        perPage,
        setPerPage,
        setPage,
        mutation: getData,
    };
};

export default useHistoryData;
